import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest, deleteRequest } from "@react/helpers/fetch"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import EmptyState from "@react/shared/ui/EmptyState"
import { formatMoney } from "./StatsStrip"

const GRID_COLUMNS = "minmax(0, 1fr) 90px 150px 140px 210px"

const asMessage = errors =>
  Array.isArray(errors) ? errors.join(", ") : String(errors)

// Short locale date for the "updated" column.
const formatDate = value => {
  if (!value) return "—"
  return new Date(value).toLocaleDateString("fr-FR", {
    day:   "2-digit",
    month: "short",
    year:  "numeric",
  })
}

const RowActions = ({ template, onUse, onDuplicate, onDelete }) => (
  <div
    className = "flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
    onClick   = {event => event.stopPropagation()}
  >
    <Button variant="secondary" size="xs" onClick={() => onUse(template)}>
      {I18n.t("quotes.project_quote_templates.row.use")}
    </Button>
    <IconButton size="md" title={I18n.t("quotes.project_quote_templates.row.duplicate")} onClick={() => onDuplicate(template)}>
      <FontAwesomeIcon icon="copy" />
    </IconButton>
    <IconButton tone="danger" size="md" title={I18n.t("quotes.project_quote_templates.row.delete")} onClick={() => onDelete(template)}>
      <FontAwesomeIcon icon="trash" />
    </IconButton>
  </div>
)

const TemplateRow = ({ template, onUse, onDuplicate, onDelete }) => (
  <div
    role      = "row"
    className = "group grid items-center gap-x-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
    onClick   = {() => { window.location.href = template.edit_url }}
  >
    <div className="min-w-0">
      <div className="font-medium text-slate-900 truncate" title={template.name}>
        {template.name}
      </div>
      {template.description && (
        <div className="text-xs text-slate-500 truncate mt-0.5" title={template.description}>
          {template.description}
        </div>
      )}
    </div>

    <div className="text-center text-slate-600 tabular-nums">{template.line_count}</div>

    <div className="text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
      {formatMoney(template.total_cents, template.currency_symbol)}
    </div>

    <div className="text-right text-sm text-slate-600 whitespace-nowrap">
      {formatDate(template.updated_at)}
    </div>

    <div className="flex items-center justify-end">
      <RowActions template={template} onUse={onUse} onDuplicate={onDuplicate} onDelete={onDelete} />
    </div>
  </div>
)

const HeaderRow = () => (
  <div
    role      = "row"
    className = "grid items-center gap-x-3 px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
  >
    <div>{I18n.t("quotes.project_quote_templates.table.name")}</div>
    <div className="text-center">{I18n.t("quotes.project_quote_templates.table.lines")}</div>
    <div className="text-right">{I18n.t("quotes.project_quote_templates.table.total")}</div>
    <div className="text-right">{I18n.t("quotes.project_quote_templates.table.updated")}</div>
    <div />
  </div>
)

const TemplatesTable = ({ templates, onUse, onRefetch }) => {
  // "Duplicate" clones server-side and jumps straight into the fresh copy.
  const handleDuplicate = template => {
    postRequest({ url: template.duplicate_url })
      .then(response => {
        if (response && response.success) {
          window.location.href = response.edit_url
          return
        }
        flashAlert(response?.errors ? asMessage(response.errors) : I18n.t("quotes.project_quote_templates.index.network_error"))
      })
      .catch(() => flashAlert(I18n.t("quotes.project_quote_templates.index.network_error")))
  }

  const handleDelete = template => {
    if (!window.confirm(I18n.t("quotes.project_quote_templates.index.delete_confirm", { name: template.name }))) return

    deleteRequest(template.destroy_url)
      .then(response => {
        if (response && response.errors) {
          flashAlert(asMessage(response.errors))
          return
        }
        flashNotice(I18n.t("quotes.project_quote_templates.index.deleted"))
        onRefetch()
      })
      .catch(() => flashAlert(I18n.t("quotes.project_quote_templates.index.network_error")))
  }

  return (
    <div className="overflow-x-auto" role="table">
      <HeaderRow />
      {templates.map(template => (
        <TemplateRow
          key         = {template.id}
          template    = {template}
          onUse       = {onUse}
          onDuplicate = {handleDuplicate}
          onDelete    = {handleDelete}
        />
      ))}
      {templates.length === 0 && (
        <EmptyState size="lg">{I18n.t("quotes.project_quote_templates.table.empty")}</EmptyState>
      )}
    </div>
  )
}

export default TemplatesTable
